// Lesson content client — talks to /api/lessons and layers a device
// cache on top so kids can re-open lessons offline (the app's core
// offline promise). Never stores drafts locally.

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CACHE_PREFIX = "we_lesson_v1:"; // + code::tier::lang

/** Map the app's age tier keys to the backend bucket. */
export function tierForAgeGroup(ageGroup) {
  if (ageGroup === "little") return "seedling";
  if (ageGroup === "older") return "pioneer";
  return "explorer";
}

function cacheKey(code, tier, language = "en") {
  return `${CACHE_PREFIX}${code}::${tier}::${language.toLowerCase()}`;
}

function readCache(code, tier, language) {
  try {
    const raw = localStorage.getItem(cacheKey(code, tier, language));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(code, tier, language, lesson) {
  try {
    localStorage.setItem(cacheKey(code, tier, language), JSON.stringify(lesson));
  } catch {
    // quota — silently ignore, we'll just refetch next time online
  }
}

/**
 * Get a published lesson for a curriculum outcome. Returns null when the
 * network is offline AND we've never cached this lesson locally.
 */
export async function fetchLesson(code, { ageTier = "explorer", language = "en", allowDraft = false } = {}) {
  const url = `${API}/lessons/${encodeURIComponent(code)}?age_tier=${encodeURIComponent(ageTier)}&language=${encodeURIComponent(language)}&allow_draft=${allowDraft ? "true" : "false"}`;
  try {
    const res = await fetch(url);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`lesson fetch failed (${res.status})`);
    const data = await res.json();
    const lesson = data.lesson;
    if (lesson) writeCache(code, ageTier, language, lesson);
    return lesson || null;
  } catch (err) {
    const cached = readCache(code, ageTier, language);
    if (cached) return cached;
    throw err;
  }
}

/** Admin — list lessons (draft + published). */
export async function listLessons({ subject = "", status = "", ageTier = "", language = "en", limit = 200 } = {}) {
  const params = new URLSearchParams({ language, limit: String(limit) });
  if (subject)  params.set("subject",   subject);
  if (status)   params.set("status",    status);
  if (ageTier)  params.set("age_tier",  ageTier);
  const res = await fetch(`${API}/lessons?${params.toString()}`);
  if (!res.ok) throw new Error(`list failed (${res.status})`);
  return res.json();
}

/** Admin — generate (or return cached draft of) one lesson. */
export async function generateLesson(payload) {
  const res = await fetch(`${API}/lessons/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `generate failed (${res.status})`);
  }
  return res.json();
}

/** Admin — batch generate an array of items sequentially on the server. */
export async function generateLessonsBatch(items, { force = false } = {}) {
  const res = await fetch(`${API}/lessons/generate-batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, force }),
  });
  if (!res.ok) throw new Error(`batch failed (${res.status})`);
  return res.json();
}

/** Admin — publish / unpublish. */
export async function setLessonStatus(code, publish, { ageTier = "explorer", language = "en" } = {}) {
  const endpoint = publish ? "publish" : "unpublish";
  const params = new URLSearchParams({ age_tier: ageTier, language });
  const res = await fetch(`${API}/lessons/${encodeURIComponent(code)}/${endpoint}?${params.toString()}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`status change failed (${res.status})`);
  return res.json();
}

/** Admin — patch a lesson (title, sections, etc.). */
export async function updateLesson(code, updates, { ageTier = "explorer", language = "en" } = {}) {
  const params = new URLSearchParams({ age_tier: ageTier, language });
  const res = await fetch(`${API}/lessons/${encodeURIComponent(code)}?${params.toString()}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`update failed (${res.status})`);
  return res.json();
}

/** Admin — bulk-flip every non-published lesson in the DB to `published`
 *  in a single Mongo query. Fast, global, no filter needed. */
export async function publishAllLessonsGlobally() {
  const res = await fetch(`${API}/lessons/publish-all`, { method: "POST" });
  if (!res.ok) throw new Error(`publish-all failed (${res.status})`);
  return res.json();
}

/** Admin — one-tap seeder that publishes a minimal starter lesson for
 *  every (tier × subject × topic) combo. Zero LLM cost. Idempotent. */
export async function seedAllSubjects({ force = false } = {}) {
  const params = new URLSearchParams();
  if (force) params.set("force", "true");
  const qs = params.toString() ? `?${params}` : "";
  const res = await fetch(`${API}/lessons/seed-all${qs}`, { method: "POST" });
  if (!res.ok) throw new Error(`seed failed (${res.status})`);
  return res.json();
}

/** Admin — one-shot rich index of every SOW topic × tier including
 *  audit / video / lesson-state signals. Powers the filter UI. */
export async function fetchAdminIndex({ language = "en" } = {}) {
  const params = new URLSearchParams({ language });
  const res = await fetch(`${API}/lessons/admin-index?${params}`);
  if (!res.ok) throw new Error(`admin-index failed (${res.status})`);
  return res.json();
}

/** Kept in sync with data/nature topics — used by the "Generate Science" admin action. */
export const NATURE_SCIENCE_OUTCOMES = [
  { code: "uk-ks1-sc-1",   subject: "Science", curriculum: "UK National Curriculum · KS1",         outcome: "Identify and name a variety of common wild and garden plants." },
  { code: "uk-ks1-sc-2",   subject: "Science", curriculum: "UK National Curriculum · KS1",         outcome: "Identify and describe the basic structure of common flowering plants." },
  { code: "uk-ks2-sc-1",   subject: "Science", curriculum: "UK National Curriculum · KS2",         outcome: "Recognise that living things can be grouped in a variety of ways." },
  { code: "uk-ks2-sc-2",   subject: "Science", curriculum: "UK National Curriculum · KS2",         outcome: "Explore and use classification keys to help group, identify and name living things." },
  { code: "uk-ks3-sc-1",   subject: "Science", curriculum: "UK National Curriculum · KS3",         outcome: "Biology: cells as the fundamental unit of living organisms; structure and function." },
  { code: "ib-pyp-sc-1",   subject: "Science", curriculum: "IB PYP",                                outcome: "Living things and their habitats — identify how living things depend on their environment." },
  { code: "ib-pyp-sc-2",   subject: "Science", curriculum: "IB PYP",                                outcome: "Materials, forces and energy — investigate simple physical changes." },
  { code: "ib-myp-sc-1",   subject: "Science", curriculum: "IB MYP",                                outcome: "Interactions of organisms and ecosystems — sustainability of Earth's biosphere." },
  { code: "cam-pri-sc-1",  subject: "Science", curriculum: "Cambridge Primary",                    outcome: "Living things: recognise similarities and differences between animals, plants and habitats." },
  { code: "cam-low-sc-1",  subject: "Science", curriculum: "Cambridge Lower Secondary",            outcome: "Cells and body systems — structure of cells and tissues." },
  { code: "us-ccss-sc-1",  subject: "Science", curriculum: "US NGSS",                              outcome: "Ecosystems: interactions, energy and dynamics." },
  { code: "us-ccss-sc-2",  subject: "Science", curriculum: "US NGSS",                              outcome: "Weather and climate — patterns over time." },
  { code: "in-cbse-sc-1",  subject: "Science", curriculum: "CBSE (India)",                         outcome: "The living world: plants, animals and their surroundings." },
  { code: "in-cbse-sc-2",  subject: "Science", curriculum: "CBSE (India)",                         outcome: "Natural resources — water, air, soil and their conservation." },
  { code: "au-ac-sc-1",    subject: "Science", curriculum: "Australian Curriculum",                outcome: "Biological sciences — living things have basic needs including food and water." },
];
